// Port of api/fuel/app/classes/filter/core/search.php
// Orchestrates the filter engine: creates query, applies filters and sorts, runs it.
// JS note:
// - folder routing in this file is an offline bootstrap concern
// - if a filter type is added here, the matching PHP filter/search tree should exist or be called out explicitly

import F from 'offline/filter/constants'
import Query from 'offline/filter/core/search/query'
import * as GenericSort from 'offline/filter/core/search/sort'
import Valid from 'offline/filter/core/common/valid'

function shouldRun(unit, config, filter, data, results) {
	if (!Valid.valid(config, filter, unit)) return false
	if (typeof unit.require !== 'function') return true
	if (results !== undefined) return unit.require(config, filter, data, results)
	return unit.require(config, filter, data)
}

function getQueryFilters(config) {
	var folder = F.TYPE_FOLDER[config.filterType]
	if (folder === 'games') {
		if (config.filterMode === F.MODE_ADVANCED) {
			return require('offline/filter/search/games/rules/query/index').queryRules()
		}
		return require('offline/filter/search/games/filters/query/index').queryFilters()
	}
	if (folder === 'users') {
		return require('offline/filter/search/users/filters/query/index').queryFilters()
	}
	if (folder === 'lists') {
		return require('offline/filter/search/lists/filters/query/index').queryFilters()
	}
	return []
}

function getQuerySorts(config) {
	var folder = F.TYPE_FOLDER[config.filterType]
	if (config.filterMode === F.MODE_ADVANCED) return []
	if (folder === 'games') {
		return require('offline/filter/search/games/sorts/query/index').querySorts()
	}
	if (folder === 'users') {
		return require('offline/filter/search/users/sorts/query/index').querySorts()
	}
	if (folder === 'lists') {
		return require('offline/filter/search/lists/sorts/query/index').querySorts()
	}
	return []
}

function getPostFilters(config) {
	var folder = F.TYPE_FOLDER[config.filterType]
	if (folder === 'games') {
		if (config.filterMode === F.MODE_ADVANCED) {
			return require('offline/filter/search/games/rules/post/index').postRules()
		}
		return require('offline/filter/search/games/filters/post/index').postFilters()
	}
	return []
}

function getPostSorts(config) {
	var folder = F.TYPE_FOLDER[config.filterType]
	if (config.filterMode === F.MODE_ADVANCED) return []
	if (folder === 'games') {
		return require('offline/filter/search/games/sorts/post/index').postSorts()
	}
	return []
}

function syncGameMappings(results) {
	if (!results || !results.index || !results.mapIndex || !results.games) return results

	results.games = results.index.map(function(id) {
		return results.mapIndex[id]
	}).filter(function(id) {
		return id !== undefined && id !== null
	})

	return results
}

function fetchCollectionNestedIndex(config, filter, results) {
	if (
		config.filterMode === F.MODE_ADVANCED ||
		config.cacheType !== F.CACHE_COLLECTION ||
		(config.ownerType !== F.OWNER_USER && !(((config.settings || {}).session || {}).collection || {}).nest) ||
		filter.nested ||
		filter.expansions !== 1 ||
		!results
	) {
		return []
	}

	var Collection = require('offline/filter/collection').default
	return Collection.forge({
		owner: config.owner,
		sessionID: config.sessionID,
		settings: config.settings,
		can: config.can,
		asIndex: true,
		filter: {
			source: {
				collection: true
			},
			nested: results.index.slice(),
			orphans: filter.orphans || [],
			users: filter.users || [],
			status: filter.status || {},
			owners: filter.owners || []
		}
	}).run()
}

function reorderAfterModifier(results, modifier) {
	if (!results || !results.index) return results

	if (modifier === 'games' && results.games && results.mapGames) {
		var newIndex = []
		var seen = {}
		var resultLookup = {}
		for (var i = 0; i < results.index.length; i++) resultLookup[results.index[i]] = true

		for (var g = 0; g < results.games.length; g++) {
			var gameId = results.games[g]
			var mapped = results.mapGames[gameId] || []
			for (var j = 0; j < mapped.length; j++) {
				var indexId = mapped[j]
				if (!seen[indexId] && resultLookup[indexId]) {
					newIndex.push(indexId)
					seen[indexId] = true
				}
			}
		}

		results.index = newIndex
	}

	if (modifier === 'index' && results.games && results.mapIndex) {
		results.games = results.index.map(function(indexId) {
			return results.mapIndex[indexId]
		}).filter(function(gameId) {
			return gameId !== undefined && gameId !== null
		})
	}

	return results
}

var Search = {

	// Main entry point. Equivalent to Filter\Core\Search::run()
	// config: { owner, ownerType, filterType, resultType, cacheType, pseudoType, users, userID, sessionID, settings, can }
	// filter: completed filter template
	// data: { total, pseudo, games, nested, users, results }
	run: function(config, filter, data) {

		// Identify orphans (mirror of PHP `filter/core/search.php:9-114`).
		// An "orphan" is a nested expansion the user owns whose base-game
		// parent does NOT satisfy the current filter. PHP elevates these to
		// the main result so the user still sees the expansion standalone
		// when filtering by status (e.g. only-fortrade) where the parent
		// base isn't fortrade. Without this, nested expansions get swept
		// up by the `c.collection_nested <= NESTED_NONE` gate in
		// `filters/query/config/nest.js` and vanish from results.
		var isAutoNest = !!(((config.settings || {}).session || {}).collection || {}).nest
		if (
			config.filterMode !== F.MODE_ADVANCED &&
			config.cacheType === F.CACHE_COLLECTION &&
			(config.ownerType === F.OWNER_USER || isAutoNest) &&
			!filter.nested && !filter.nestParent && !filter.nestChild
		) {
			// Use an INLINE GamesFilter (mirrors PHP `Filter::search` which
			// uses CACHE_INLINE) so `config/nest.js` — gated on
			// CACHE_COLLECTION — doesn't fire during the sub-search and
			// contradict the `nestChild: true` / `nestParent: true` gate we
			// set here.
			var InlineSearchFilter = require('offline/filter/inline-search').default

			var subIndex = function(extra) {
				return InlineSearchFilter.forge({
					owner: config.owner,
					ownerType: config.ownerType,
					sessionID: config.sessionID,
					settings: config.settings,
					can: config.can,
					asIndex: true,
					filter: Object.assign({
						source: { collection: true },
						text: filter.text || '',
						status: filter.status || {},
						tags: filter.tags || [],
						users: filter.users || [],
						owners: filter.owners || [],
						nosort: true
					}, extra)
				}).run() || []
			}

			var baseIndex, expansionIndex, expansionBaseIndex

			if (config.ownerType === F.OWNER_USER) {
				baseIndex = subIndex({ nestParent: true })
				expansionIndex = subIndex({ nestChild: true })
				if (!baseIndex.length) baseIndex = [0]
				expansionBaseIndex = subIndex({ nested: baseIndex })
			} else {
				baseIndex = subIndex({ expansions: 0 })
				expansionIndex = subIndex({ expansions: 2 })
				expansionBaseIndex = subIndex({ base: baseIndex, expansions: 2 })
			}

			var baseIds = {}
			for (var bi = 0; bi < expansionBaseIndex.length; bi++) baseIds[expansionBaseIndex[bi]] = true

			var orphans
			if (filter.expansions === 2) {
				orphans = expansionIndex.concat(expansionBaseIndex)
			} else {
				orphans = []
				for (var ei = 0; ei < expansionIndex.length; ei++) {
					if (!baseIds[expansionIndex[ei]]) orphans.push(expansionIndex[ei])
				}
			}

			var orphanSeen = {}
			var orphanDedup = []
			for (var oi = 0; oi < orphans.length; oi++) {
				var oid = parseInt(orphans[oi], 10)
				if (!oid || orphanSeen[oid]) continue
				orphanSeen[oid] = true
				orphanDedup.push(oid)
			}
			filter.orphans = orphanDedup
		}

		// Pre-fetch expansion count matches (mirror of PHP
		// `filter/core/search.php:117-156`). When a count.* range is
		// active, PHP runs a nested Filter::search over the user's
		// collection with `nested: true` + `overload: true` to find base
		// games whose EXPANSIONS satisfy the count criteria — those bases
		// are added to `filter.count.whitelist` and OR'd into the main
		// count filter (see `filters/query/game/count/official.js`).
		// Without this whitelist, count filters drop base games that
		// would otherwise surface via their expansion's player-count.
		if (
			config.filterMode !== F.MODE_ADVANCED &&
			config.cacheType === F.CACHE_COLLECTION &&
			(((config.settings || {}).session || {}).collection || {}).nest &&
			!filter.nested &&
			filter.expansions &&
			filter.count && filter.count.nested &&
			(
				filter.count.best && (filter.count.best.from !== 1 || filter.count.best.to !== 10) ||
				filter.count.official && (filter.count.official.from !== 1 || filter.count.official.to !== 10) ||
				filter.count.recommended && (filter.count.recommended.from !== 1 || filter.count.recommended.to !== 10)
			)
		) {
			var CollectionFilter = require('offline/filter/collection').default
			var whitelistIndex = CollectionFilter.forge({
				owner: config.owner,
				ownerType: config.ownerType,
				sessionID: config.sessionID,
				settings: config.settings,
				can: config.can,
				asIndex: true,
				filter: {
					source: { collection: true },
					overload: true,
					nested: true,
					orphans: filter.orphans || [],
					count: filter.count,
					threshold: { count: (filter.threshold || {}).count },
					users: filter.users || [],
					owners: filter.owners || []
				}
			}).run()
			var seen = {}
			var dedup = []
			for (var wi = 0; wi < (whitelistIndex || []).length; wi++) {
				var wid = parseInt(whitelistIndex[wi], 10)
				if (!wid || seen[wid]) continue
				seen[wid] = true
				dedup.push(wid)
			}
			filter.count.whitelist = dedup
		}

		var query = Query.forge()
		if (config.filterMode === F.MODE_ADVANCED && F.TYPE_FOLDER[config.filterType] === 'games') {
			query.join('game', 'g')
		}
		if (Array.isArray(config.seedIndex)) {
			var relation = F.RESULT_RELATION[config.resultType]
			var table = F.RESULT_TABLE[config.resultType]
			if (relation && table) {
				query.where(relation + '.' + table + '_id', 'in', config.seedIndex.length ? config.seedIndex : [0])
			}
		}

		// Apply all query filters that pass their require() check
		var filters = getQueryFilters(config)
		for (var fi = 0; fi < filters.length; fi++) {
			var f = filters[fi]
			if (shouldRun(f, config, filter, data)) {
				f.run(config, filter, data, query)
			}
		}

		// Apply all query sorts that pass their require() check
		var sorts = getQuerySorts(config)
		var postSorts = getPostSorts(config)
		var postSortCandidates = []
		var postSortProcessors = {}

		for (var psi = 0; psi < postSorts.length; psi++) {
			var supported = postSorts[psi].sorts ? postSorts[psi].sorts(config, filter) : []
			for (var sp = 0; sp < supported.length; sp++) {
				postSortProcessors[supported[sp]] = postSorts[psi]
				postSortCandidates.push(supported[sp])
			}
		}

		for (var si = 0; si < sorts.length; si++) {
			var s = sorts[si]
			if (shouldRun(s, config, filter, data)) {
				s.run(config, filter, data, query)
			}
		}

		// If there are mixed query+post sorts, go orderless
		var activeSorts = filter.sort ? filter.sort.replace(/-/g, '').split(',') : []
		var applicablePostSorts = postSortCandidates.filter(function(name) {
			return activeSorts.indexOf(name) !== -1
		})
		var processOrdering = applicablePostSorts.length > 0 && applicablePostSorts.length < activeSorts.length
		if (processOrdering) query.orderless()

		// Execute the query
		var results = query.run(config, filter, data)

		// Apply post filters
		var postFilters = getPostFilters(config)
		for (var pfi = 0; pfi < postFilters.length; pfi++) {
			var postFilter = postFilters[pfi]
			results.sortvalue = {}
			for (var pfr = 0; pfr < results.index.length; pfr++) {
				results.sortvalue[results.index[pfr]] = ''
			}
			if (results.index.length && shouldRun(postFilter, config, filter, data, results)) {
				results = postFilter.run(config, filter, data, results)
				results = reorderAfterModifier(results, postFilter.modifies ? postFilter.modifies(config, filter, data, results) : 'index')
			}
		}

		// Apply post sorts
		if (processOrdering && results.index.length) {
			var priorityIndex = {}
			var metaKeys = Object.keys(results.meta || {})
			for (var mk = 0; mk < metaKeys.length; mk++) {
				var matches = metaKeys[mk].match(/^order_([0-9]+)_(asc|desc)_([0-9]+)$/)
				if (!matches) continue
				if (!priorityIndex[matches[3]]) priorityIndex[matches[3]] = []
				priorityIndex[matches[3]].push(metaKeys[mk])
			}

			for (var aps = 0; aps < applicablePostSorts.length; aps++) {
				var keyIndex = activeSorts.indexOf(applicablePostSorts[aps]) + 1
				if (!priorityIndex[keyIndex]) priorityIndex[keyIndex] = []
				priorityIndex[keyIndex].push(applicablePostSorts[aps])
			}

			results.sortvalue = {}
			for (var ri = 0; ri < results.index.length; ri++) results.sortvalue[results.index[ri]] = ''

			Object.keys(priorityIndex).sort(function(a, b) { return parseInt(a, 10) - parseInt(b, 10) }).forEach(function(groupKey) {
				var priorityGroup = priorityIndex[groupKey]
				for (var pg = 0; pg < priorityGroup.length; pg++) {
					var sortName = priorityGroup[pg]
					var processor = postSortProcessors[sortName]
					var modifier = processor ? (processor.modifies ? processor.modifies(config, filter, data, results) : 'index') : GenericSort.modifies()
					var groupedIndexes = {}

					Object.keys(results.sortvalue).forEach(function(key) {
						var groupedKey = modifier === 'games' ? results.mapIndex[key] : parseInt(key, 10)
						var sortvalue = results.sortvalue[key]
						if (!groupedIndexes[sortvalue]) groupedIndexes[sortvalue] = []
						groupedIndexes[sortvalue].push(groupedKey)
					})

					var groupResults = processor ? processor.run(config, filter, data, results) : GenericSort.run(sortName, results)
					var lookupSource = modifier === 'games' ? groupResults.games : groupResults.index
					var groupResultsLookup = {}
					for (var li = 0; li < lookupSource.length; li++) groupResultsLookup[lookupSource[li]] = li

					var overallIndex = 0
					Object.keys(groupedIndexes).forEach(function(sortvalueKey) {
						var groupIndex = groupedIndexes[sortvalueKey]
						groupIndex.sort(function(a, b) {
							var av = groupResultsLookup[a]
							var bv = groupResultsLookup[b]
							if (av === undefined) av = Number.MAX_SAFE_INTEGER
							if (bv === undefined) bv = Number.MAX_SAFE_INTEGER
							return av - bv
						})
						for (var gi = 0; gi < groupIndex.length; gi++) {
							results[modifier === 'games' ? 'games' : 'index'][overallIndex] = groupIndex[gi]
							overallIndex++
						}
					})

					results.sortvalue = groupResults.sortvalue
					results = reorderAfterModifier(results, modifier)
				}
			})
		} else {
			for (var psi2 = 0; psi2 < postSorts.length; psi2++) {
				var postSort = postSorts[psi2]
				results.sortvalue = {}
				for (var psr = 0; psr < results.index.length; psr++) {
					results.sortvalue[results.index[psr]] = ''
				}
				if (results.index.length && shouldRun(postSort, config, filter, data, results)) {
					results = postSort.run(config, filter, data, results)
					results = reorderAfterModifier(results, postSort.modifies ? postSort.modifies(config, filter, data, results) : 'index')
				}
			}
		}

		results = syncGameMappings(results)
		var nestedIndex = fetchCollectionNestedIndex(config, filter, results)

		// Build final data
		data.total = results.index.length
		data.results = results.index
		data.games = results.games || []
		data.nested = nestedIndex
		data.meta = results.meta
		data.mapGames = results.mapGames || {}
		data.mapIndex = results.mapIndex || {}
		data.mapPseudo = results.mapPseudo || {}

		// Pseudo count — only sum entries still in the post-filtered index
		var pseudoCount = 0
		var mapPseudo = results.mapPseudo || {}
		for (var pi = 0; pi < data.results.length; pi++) {
			var pseudoVal = mapPseudo[data.results[pi]]
			if (pseudoVal) pseudoCount += pseudoVal
		}
		data.pseudo = pseudoCount

		return data
	}

}

export default Search
